import { indexes } from '@mab-common/utils'

import type { MsearchRequest } from '@elastic/elasticsearch/lib/api/types'

import {
  buildAggsCountField,
  buildElasticResultSourceFields,
  buildElasticSearchTerm,
  buildElasticSortFields,
} from '~/services/elastic/builder'

import { MIN_SCORES } from './constants'

export function buildArticleArtistFacet(aggsHit: any) {
  return {
    title: 'Artists',
    facets:
      aggsHit?.buckets?.map((bucket: any) => {
        const doc = bucket?.docs?.hits?.hits?.[0]?._source
        return {
          id: bucket.key,
          name: doc?.articleTypeTitle || bucket.key,
          slug: bucket.key,
          count: bucket.doc_count,
        }
      }) ?? [],
  }
}

export function buildArticleCategoryFacet(aggsHit: any) {
  return {
    title: 'Categories',
    facets:
      aggsHit?.buckets?.map((bucket: any) => {
        const doc = bucket?.docs?.hits?.hits?.[0]?._source
        // Find the category that matches the bucket key (slug)
        const category = doc?.secondaryCategories?.find(
          (cat: any) => cat.slug === bucket.key
        )
        return {
          id: bucket.key,
          name: category?.name || bucket.key,
          slug: bucket.key,
          count: bucket.doc_count,
        }
      }) ?? [],
  }
}

const { RecordTypes, EsIndexTypes } = indexes

export function buildSearchQuery(
  pageFrom: number,
  pageSize: number,
  recordType: indexes.RecordTypes,
  searchTerm?: string,
  filters?: any, // You might want to type this properly
  locale = 'en',
  queriedId?: string,
  queriedType?: 'artist' | 'collection' | 'genre'
): MsearchRequest {
  // Define the common base configuration
  const baseConfig = {
    sort: buildElasticSortFields(recordType),
    _source: buildElasticResultSourceFields(recordType),
  }

  switch (recordType) {
    case RecordTypes.ARTIST:
      // Your existing code for ARTIST
      return {
        index: EsIndexTypes.ARTIST,
        searches: [
          {},
          {
            from: pageFrom,
            size: pageSize,
            sort: buildElasticSortFields(RecordTypes.ARTIST),
            _source: buildElasticResultSourceFields(recordType),
            min_score: MIN_SCORES.ARTIST_SEARCH,
            query: {
              bool: {
                must: buildElasticSearchTerm(RecordTypes.ARTIST, searchTerm),
              },
            },
          },
        ],
      }
    case RecordTypes.COLLECTION:
      // Your existing code for COLLECTION
      return {
        index: EsIndexTypes.COLLECTION,
        searches: [
          {},
          {
            from: pageFrom,
            size: pageSize,
            sort: buildElasticSortFields(RecordTypes.COLLECTION),
            _source: buildElasticResultSourceFields(recordType),
            query: {
              bool: {
                must: buildElasticSearchTerm(
                  RecordTypes.COLLECTION,
                  searchTerm
                ),
              },
            },
          },
        ],
      }
    case RecordTypes.PODCASTS:
    case RecordTypes.VIDEOS:
    case RecordTypes.ARTICLES: {
      // Enhanced code for ARTICLES to include facets
      // Build filters for ARTICLES
      // const artistFilters =
      //   filters?.artists?.length > 0
      //     ? [{ terms: { 'artistSlug.keyword': filters.artists } }] // Use .keyword for terms queries
      //     : []

      // const categoryFilters =
      //   filters?.categories?.length > 0
      //     ? [
      //         {
      //           terms: {
      //             'secondaryCategories.slug.keyword': filters.categories,
      //           },
      //         },
      //       ]
      //     : []
      const artistFilters = []
      if (filters?.artist) {
        artistFilters.push({ term: { 'artistSlug.keyword': filters.artist } })
      }

      // Handle category filter as a single string in the 'filter' field
      const categoryFilters = []
      if (filters?.filter) {
        categoryFilters.push({
          term: { 'secondaryCategories.slug.keyword': filters.filter },
        })
      }

      // Common must queries
      const mustQueries = buildElasticSearchTerm(recordType, searchTerm)

      // Language filter for articles
      const langFilter =
        locale === 'en'
          ? {
              bool: {
                should: [
                  { term: { lang: 'en' } },
                  { bool: { must_not: { exists: { field: 'lang' } } } },
                ],
                minimum_should_match: 1,
              },
            }
          : { term: { lang: locale } }

      // Aggregation size
      const aggsRandSize = 1000

      // console.log('query articles', {
      //   ...baseConfig,
      //   from: pageFrom,
      //   size: pageSize,
      //   query: {
      //     bool: {
      //       filter: [
      //         { term: { type: 'article' } },
      //         ...artistFilters,
      //         ...categoryFilters,
      //       ],
      //       must: mustQueries,
      //     },
      //   },
      // })

      return {
        index: EsIndexTypes.ARTICLES,
        searches: [
          // Main search for article results
          {},
          {
            ...baseConfig,
            from: pageFrom,
            size: pageSize,
            query: {
              bool: {
                filter: [
                  { term: { type: 'article' } },
                  langFilter,
                  ...artistFilters,
                  ...categoryFilters,
                ],
                must: mustQueries,
              },
            },
          },

          // Artist aggregation - using .keyword field
          { index: EsIndexTypes.ARTICLES },
          {
            ...baseConfig,
            size: 0,
            query: {
              bool: {
                filter: [
                  { term: { type: 'article' } },
                  langFilter,
                  ...categoryFilters,
                ],
                must: mustQueries,
              },
            },
            aggs: {
              [buildAggsCountField('artist')]: {
                cardinality: { field: 'artistSlug.keyword' },
              },
              artist: {
                terms: { field: 'artistSlug.keyword', size: aggsRandSize },
                aggs: {
                  docs: {
                    top_hits: {
                      size: 1,
                      _source: ['artistSlug', 'articleTypeTitle'],
                    },
                  },
                },
              },
            },
          },

          // Category aggregation
          { index: EsIndexTypes.ARTICLES },
          {
            ...baseConfig,
            size: 0,
            query: {
              bool: {
                filter: [
                  { term: { type: 'article' } },
                  langFilter,
                  ...artistFilters,
                ],
                must: mustQueries,
              },
            },
            aggs: {
              [buildAggsCountField('category')]: {
                cardinality: { field: 'secondaryCategories.slug.keyword' },
              },
              category: {
                terms: {
                  field: 'secondaryCategories.slug.keyword',
                  size: aggsRandSize,
                },
                aggs: {
                  docs: {
                    top_hits: {
                      size: 1,
                      _source: ['secondaryCategories'],
                    },
                  },
                },
              },
            },
          },
        ],
      }
    }

    case RecordTypes.ARTWORK:
    default: {
      // Build filters based on provided parameters
      const genreFilters =
        filters?.genres?.length > 0
          ? [{ terms: { 'genres._id.keyword': filters.genres } }]
          : []

      const formatFilters =
        filters?.formats?.length > 0
          ? [{ terms: { 'format._id.keyword': filters.formats } }]
          : []

      const artistFilters =
        filters?.artists?.length > 0
          ? [{ terms: { artistId: filters.artists } }]
          : []

      const priceFilters =
        filters?.prices?.length > 1
          ? [
              {
                range: {
                  listPriceMin: {
                    gte: +filters.prices[0],
                    lte: +filters.prices[1],
                  },
                },
              },
            ]
          : []

      const queriedFilters =
        queriedId && queriedType
          ? [
              {
                term: {
                  [queriedType === 'genre'
                    ? 'genres._id.keyword'
                    : queriedType === 'artist'
                      ? 'artistId'
                      : 'collectionId.keyword']: queriedId,
                },
              },
            ]
          : []

      // Base filter queries
      const filterQueries = [{ term: { type: 'artwork' } }, ...queriedFilters]

      // Common must_not queries
      const mustNotQueries = [{ term: { isVisible: false } }]

      // Common must queries
      const mustQueries = buildElasticSearchTerm(
        RecordTypes.ARTWORK,
        searchTerm
      )

      // Aggregation size
      const aggsRandSize = 1000

      // Return the configured msearch query with all searches and aggregations
      return {
        index: EsIndexTypes.ARTWORKS,
        searches: [
          // Main search for artwork results
          {},
          {
            ...baseConfig,
            from: pageFrom,
            size: pageSize,
            sort: buildElasticSortFields(RecordTypes.ARTWORK),
            _source: buildElasticResultSourceFields(RecordTypes.ARTWORK),
            query: {
              bool: {
                must: mustQueries,
                must_not: mustNotQueries,
                filter: [
                  ...filterQueries,
                  ...formatFilters,
                  ...genreFilters,
                  ...artistFilters,
                  ...priceFilters,
                ],
              },
            },
          },

          // Artist aggregation
          { index: EsIndexTypes.ARTWORKS },
          {
            ...baseConfig,
            size: 0,
            query: {
              bool: {
                must: mustQueries,
                must_not: mustNotQueries,
                filter: [
                  ...filterQueries,
                  ...formatFilters,
                  ...genreFilters,
                  ...priceFilters,
                ],
              },
            },
            aggs: {
              [buildAggsCountField('artist')]: {
                cardinality: { field: 'artistId' },
              },
              artist: {
                terms: { field: 'artistId', size: aggsRandSize },
                aggs: {
                  docs: {
                    top_hits: {
                      size: 1,
                      _source: ['artist', 'artistId', 'artistSlug'],
                    },
                  },
                },
              },
            },
          },

          // Price aggregation
          { index: EsIndexTypes.ARTWORKS },
          {
            ...baseConfig,
            size: 0,
            query: {
              bool: {
                must: mustQueries,
                must_not: mustNotQueries,
                filter: [
                  ...filterQueries,
                  ...formatFilters,
                  ...genreFilters,
                  ...artistFilters,
                  {
                    term: {
                      poa: false,
                    },
                  },
                ],
              },
            },
            aggs: {
              price: {
                terms: { field: 'listPriceMin', size: aggsRandSize },
              },
            },
          },

          // Format aggregation
          { index: EsIndexTypes.ARTWORKS },
          {
            ...baseConfig,
            size: 0,
            query: {
              bool: {
                must: mustQueries,
                must_not: mustNotQueries,
                filter: [
                  ...filterQueries,
                  ...genreFilters,
                  ...artistFilters,
                  ...priceFilters,
                ],
              },
            },
            aggs: {
              [buildAggsCountField('format')]: {
                cardinality: { field: 'format._id.keyword' },
              },
              format: {
                terms: { field: 'format._id.keyword', size: aggsRandSize },
                aggs: {
                  docs: {
                    top_hits: {
                      size: 1,
                      _source: ['format._id', 'format.name', 'format.slug'],
                    },
                  },
                },
              },
            },
          },

          // Genre aggregation
          { index: EsIndexTypes.ARTWORKS },
          {
            ...baseConfig,
            size: 0,
            query: {
              bool: {
                must: mustQueries,
                must_not: mustNotQueries,
                filter: [
                  ...filterQueries,
                  ...formatFilters,
                  ...artistFilters,
                  ...priceFilters,
                ],
              },
            },
            aggs: {
              [buildAggsCountField('genre')]: {
                cardinality: { field: 'genres._id.keyword' },
              },
              genre: {
                terms: { field: 'genres._id.keyword', size: aggsRandSize },
                aggs: {
                  docs: {
                    top_hits: {
                      size: 1,
                      _source: ['genres._id', 'genres.name', 'genres.slug'],
                    },
                  },
                },
              },
            },
          },
        ],
      }
    }
  }
}

// import { indexes } from '@mab-common/utils'

// import type { MsearchRequest } from '@elastic/elasticsearch/lib/api/types'

// import {
//   buildElasticResultSourceFields,
//   buildElasticSearchTerm,
//   buildElasticSortFields,
// } from '~/services/elastic/builder'

// const { RecordTypes, EsIndexTypes } = indexes

// export function buildSearchQuery(
//   pageFrom: number,
//   pageSize: number,
//   recordType: indexes.RecordTypes,
//   searchTerm?: string
// ): MsearchRequest {
//   switch (recordType) {
//     case RecordTypes.ARTIST:
//       return {
//         index: EsIndexTypes.ARTIST,
//         searches: [
//           {},
//           {
//             from: pageFrom,
//             size: pageSize,
//             sort: buildElasticSortFields(RecordTypes.ARTIST),
//             _source: buildElasticResultSourceFields(recordType),
//             min_score: 0.5,
//             query: {
//               bool: {
//                 must: buildElasticSearchTerm(RecordTypes.ARTIST, searchTerm),
//               },
//             },
//           },
//         ],
//       }
//     case RecordTypes.COLLECTION:
//       return {
//         index: EsIndexTypes.COLLECTION,
//         searches: [
//           {},
//           {
//             from: pageFrom,
//             size: pageSize,
//             sort: buildElasticSortFields(RecordTypes.COLLECTION),
//             _source: buildElasticResultSourceFields(recordType),
//             query: {
//               bool: {
//                 must: buildElasticSearchTerm(
//                   RecordTypes.COLLECTION,
//                   searchTerm
//                 ),
//               },
//             },
//           },
//         ],
//       }
//     case RecordTypes.ARTICLES:
//       return {
//         index: EsIndexTypes.ARTICLES,
//         searches: [
//           {},
//           {
//             from: pageFrom,
//             size: pageSize,
//             sort: buildElasticSortFields(RecordTypes.ARTICLES),
//             _source: buildElasticResultSourceFields(recordType),
//             query: {
//               bool: {
//                 filter: [{ term: { type: 'article' } }],
//                 must: buildElasticSearchTerm(RecordTypes.ARTICLES, searchTerm),
//               },
//             },
//           },
//         ],
//       }
//     case RecordTypes.ARTWORK:
//     default:
//       return {
//         index: EsIndexTypes.ARTWORKS,
//         searches: [
//           {},
//           {
//             from: pageFrom,
//             size: pageSize,
//             sort: buildElasticSortFields(RecordTypes.ARTWORK),
//             _source: buildElasticResultSourceFields(RecordTypes.ARTWORK),
//             query: {
//               bool: {
//                 must_not: [{ term: { isVisible: false } }],
//                 filter: [{ term: { type: 'artwork' } }],
//                 must: buildElasticSearchTerm(RecordTypes.ARTWORK, searchTerm),
//               },
//             },
//           },
//         ],
//       }
//   }
// }

export function buildInstaSearchQuery(
  pageFrom: number,
  pageSize: number,
  searchTerm?: string,
  locale = 'en'
): MsearchRequest {
  return {
    index: EsIndexTypes.ARTIST,
    searches: [
      {},
      {
        from: pageFrom,
        size: pageSize,
        sort: searchTerm
          ? buildElasticSortFields(RecordTypes.ARTIST)
          : {
              totalArtworks: {
                order: 'desc',
              },
            },
        _source: buildElasticResultSourceFields(RecordTypes.ARTIST),
        min_score: MIN_SCORES.ARTIST_INSTANT_SEARCH,
        highlight: searchTerm
          ? {
              fields: {
                name: {},
                suggestText: {},
              },
              pre_tags: ['<strong>'],
              post_tags: ['</strong>'],
            }
          : undefined,
        query: {
          bool: {
            must: buildElasticSearchTerm(RecordTypes.ARTIST, searchTerm),
          },
        },
      },
      { index: EsIndexTypes.COLLECTION },
      {
        from: pageFrom,
        size: pageSize,
        sort: buildElasticSortFields(RecordTypes.COLLECTION),
        _source: buildElasticResultSourceFields(RecordTypes.COLLECTION),
        highlight: searchTerm
          ? {
              fields: {
                title: {},
                artist: {},
              },
              pre_tags: ['<strong>'],
              post_tags: ['</strong>'],
            }
          : undefined,
        query: {
          bool: {
            must: buildElasticSearchTerm(RecordTypes.COLLECTION, searchTerm),
          },
        },
      },
      { index: EsIndexTypes.ARTWORKS },
      {
        from: pageFrom,
        size: pageSize,
        sort: buildElasticSortFields(RecordTypes.ARTWORK),
        _source: buildElasticResultSourceFields(RecordTypes.ARTWORK),
        highlight: searchTerm
          ? {
              fields: {
                title: {},
                artist: {},
                suggestText: {},
              },
              pre_tags: ['<strong>'],
              post_tags: ['</strong>'],
            }
          : undefined,
        query: {
          bool: {
            must_not: [{ term: { isVisible: false } }],
            must: buildElasticSearchTerm(RecordTypes.ARTWORK, searchTerm),
          },
        },
      },
      { index: EsIndexTypes.ARTICLES },
      {
        from: pageFrom,
        size: pageSize,
        sort: buildElasticSortFields(RecordTypes.ARTICLES),
        _source: buildElasticResultSourceFields(RecordTypes.ARTICLES),
        highlight: searchTerm
          ? {
              fields: {
                title: {},
                articleSummary: {},
                author: {},
              },
              pre_tags: ['<strong>'],
              post_tags: ['</strong>'],
            }
          : undefined,
        query: {
          bool: {
            filter: [
              locale === 'en'
                ? {
                    bool: {
                      should: [
                        { term: { lang: 'en' } },
                        { bool: { must_not: { exists: { field: 'lang' } } } },
                      ],
                      minimum_should_match: 1,
                    },
                  }
                : { term: { lang: locale } },
            ],
            must: buildElasticSearchTerm(RecordTypes.ARTICLES, searchTerm),
          },
        },
      },
      { index: EsIndexTypes.ARTICLES },
      {
        from: pageFrom,
        size: pageSize,
        sort: buildElasticSortFields(RecordTypes.PODCASTS_AND_VIDEOS),
        _source: buildElasticResultSourceFields(
          RecordTypes.PODCASTS_AND_VIDEOS
        ),
        highlight: searchTerm
          ? {
              fields: {
                title: {},
                articleSummary: {},
                author: {},
              },
              pre_tags: ['<strong>'],
              post_tags: ['</strong>'],
            }
          : undefined,
        query: {
          bool: {
            filter: [
              locale === 'en'
                ? {
                    bool: {
                      should: [
                        { term: { lang: 'en' } },
                        { bool: { must_not: { exists: { field: 'lang' } } } },
                      ],
                      minimum_should_match: 1,
                    },
                  }
                : { term: { lang: locale } },
            ],
            must: buildElasticSearchTerm(
              RecordTypes.PODCASTS_AND_VIDEOS,
              searchTerm
            ),
          },
        },
      },
    ],
  }
}

// export function buildPortfolioFeed(
//   pageFrom: number,
//   pageSize: number,
//   artworkIds?: string[]
// ): MsearchRequest {
//   return {
//     index: EsIndexTypes.ARTWORKS,
//     searches: [
//       {},
//       { index: EsIndexTypes.ARTWORKS },
//       {
//         from: pageFrom,
//         size: pageSize,
//         sort: buildElasticSortFields(RecordTypes.ARTWORK),
//         _source: buildElasticResultSourceFields(RecordTypes.ARTWORK),
//         query: {
//           bool: {
//             must_not: [{ term: { isVisible: false } }],
//             must: buildElasticSearchTerm(RecordTypes.ARTWORK, searchTerm),
//           },
//         },
//       },
//     ],
//   }
// }
